import type {
  FieldCatalogEntry,
  RuleBuilderConfig,
  RuleBuilderOption,
  ValueKind,
} from '@packages/ui/patterns/CriteriaRuleBuilder';

export type RuleBuilderOptionFetchPlan = {
  actionId: string;
  requires: string[];
  labelField: string;
  valueField: string;
  abbreviationField?: string;
};

function readFormPath(formValues: Record<string, unknown>, path: string): unknown {
  if (!path.includes('.')) return formValues[path];
  return path.split('.').reduce<unknown>((acc, segment) => {
    if (acc == null || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[segment];
  }, formValues);
}

/** Build string params for catalog + option actions from config.actionContext. */
export function buildRuleBuilderActionParams(
  config: RuleBuilderConfig,
  formValues: Record<string, unknown>,
  blockParams?: Record<string, unknown>,
): Record<string, string> {
  const ctx = config.actionContext;
  const merged: Record<string, string> = {};

  if (ctx?.includeBlockParams !== false) {
    for (const [key, val] of Object.entries(blockParams ?? {})) {
      if (val != null && val !== '') merged[key] = String(val);
    }
  }

  for (const field of ctx?.formFields ?? []) {
    const val = formValues[field];
    if (val != null && val !== '') merged[field] = String(val);
  }

  for (const [paramKey, formPath] of Object.entries(ctx?.paramMap ?? {})) {
    const val = readFormPath(formValues, formPath);
    if (val != null && val !== '') merged[paramKey] = String(val);
  }

  return merged;
}

function planFromFetch(fetch: NonNullable<FieldCatalogEntry['fetch']>): RuleBuilderOptionFetchPlan {
  return {
    actionId: fetch.actionId,
    requires: [],
    labelField: fetch.labelField ?? 'label',
    valueField: fetch.valueField ?? 'value',
  };
}

function planFromValueKindResolver(
  resolver: NonNullable<RuleBuilderConfig['valueKindResolvers']>[ValueKind],
): RuleBuilderOptionFetchPlan | null {
  if (!resolver?.actionId) return null;
  return {
    actionId: resolver.actionId,
    requires: resolver.requires ?? [],
    labelField: resolver.labelField ?? 'label',
    valueField: resolver.valueField ?? 'value',
    abbreviationField: resolver.abbreviationField,
  };
}

/** Resolve which action loads select options for a catalog row (entry overrides config). */
export function getRuleBuilderOptionFetchPlan(
  config: RuleBuilderConfig,
  entry: FieldCatalogEntry,
): RuleBuilderOptionFetchPlan | null {
  if (entry.staticOptions?.length) return null;
  if (entry.fetch?.actionId) return planFromFetch(entry.fetch);
  const byKind = config.valueKindResolvers?.[entry.valueKind];
  return planFromValueKindResolver(byKind ?? undefined);
}

export function mapRowsToRuleBuilderOptions(
  rows: unknown,
  plan: RuleBuilderOptionFetchPlan,
): RuleBuilderOption[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => {
    const record = row as Record<string, unknown>;
    const label = String(record[plan.labelField] ?? '');
    const value = String(record[plan.valueField] ?? '');
    const option: RuleBuilderOption = { label, value };
    if (plan.abbreviationField) {
      const abbr = record[plan.abbreviationField];
      if (abbr != null && abbr !== '') option.abbreviation = String(abbr);
    }
    return option;
  });
}

export function resolveCatalogActionId(
  config: RuleBuilderConfig,
  overrideActionId?: string,
): string | undefined {
  if (overrideActionId) return overrideActionId;
  if (config.fieldCatalog?.source === 'action') return config.fieldCatalog.actionId;
  return undefined;
}
